/**
 * Role-based Authentication Utilities
 * Handles token management for customer, host, and admin roles
 */

// Get current role based on URL path
export const getCurrentRole = () => {
  const path = window.location.pathname;

  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/host')) return 'host';
  if (path.startsWith('/partner')) return 'partner';
  return 'customer';
};

// Get token for current role
export const getToken = () => {
  const role = getCurrentRole();
  
  switch (role) {
    case 'admin':
      return localStorage.getItem('admin_token');
    case 'host':
      return localStorage.getItem('user_token'); // Hosts use user_token
    case 'customer':
    default:
      return localStorage.getItem('user_token'); // Customers use user_token
  }
};

// Get user data for current role
export const getUserData = () => {
  try {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

// Check if user has specific role
export const hasRole = (role) => {
  const userData = getUserData();
  if (!userData || !userData.roles) return false;
  
  return userData.roles.includes(role);
};

// Check if user can access current section
export const canAccessSection = () => {
  const currentRole = getCurrentRole();
  const userData = getUserData();
  
  if (!userData) return false;
  
  // Admin can access admin section if they have admin role
  if (currentRole === 'admin') {
    return userData.roles && userData.roles.includes('admin');
  }
  
  // Host can access host section if they have host role
  if (currentRole === 'host') {
    return userData.roles && userData.roles.includes('host');
  }

  // Partner can access partner section if they have partner role
  if (currentRole === 'partner') {
    return userData.roles && userData.roles.includes('partner');
  }

  // Everyone can access customer section (booking properties)
  return true;
};

// Redirect to appropriate dashboard based on roles
export const getDefaultDashboard = () => {
  const userData = getUserData();
  
  if (!userData || !userData.roles) return '/';
  
  // Priority: admin > customer
  if (userData.roles.includes('admin')) return '/admin/dashboard';
  return '/dashboard'; // Customer dashboard
};

// Check if user is authenticated (checks both user_token and admin_token)
export const isAuthenticated = () => {
  const userToken = localStorage.getItem('user_token');
  const adminToken = localStorage.getItem('admin_token');
  return !!(userToken || adminToken);
};

// Logout (clear all tokens)
export const logout = () => {
  localStorage.removeItem('user_token');
  localStorage.removeItem('admin_token');
  localStorage.removeItem('user_data');
  window.location.href = '/login';
};

// Get authorization header for API calls
export const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
